import { isValidEmail, isValidIsraeliId } from "utils/fieldValidators";
import type { CrmCellDraft, CrmEditableDimension } from "./crmCellEditModel";

/**
 * The ONLY two values in this editor that a realtor can get objectively wrong, and what
 * the surface says about them.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * WHY VALIDATION EXISTS HERE AT ALL, WHEN THE SERVER DELIBERATELY HAS NONE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * `contactWriteRules.ts` accepts `mail` and `tehudatZehut` as plain trimmed strings and
 * says why in its own comment: the corpus is full of lenient-era imports, and refusing a
 * whole save over a neighbouring legacy value is what made the old form's save button
 * "randomly not clickable". That is the right call for a WRITE ENDPOINT, which sees a
 * patch and cannot know whether the offending value is the one being edited.
 *
 * An editor knows. It is showing one field, the realtor is typing in it right now, and a
 * mistyped address is a message that will never arrive. `EditContactDialog` blocks its
 * save for the same reason, so a popup that silently accepted what the dialog on the same
 * screen refuses would be the app disagreeing with itself about the same field.
 *
 * ── The rule: REFUSE ONCE, THEN DISCARD ─────────────────────────────────────────
 *
 * A dismissal of an INVALID value does not save and does not close — it turns the field
 * red and says what is wrong. A SECOND dismissal cancels, throwing the bad value away.
 * So the realtor is told once, and is never trapped: two clicks always get out, and the
 * value that reaches the document is either correct or was never written.
 *
 * ⚠️ THE CHECK MUST NOT LIVE INSIDE `commit`. Three reasons, each independently fatal:
 *
 *  1. The UNMOUNT FLUSH bypasses `commit` entirely — it calls `buildCellPatch` and `send`
 *     directly — so a guard there would be silently skipped on a route change and the
 *     invalid value would be persisted anyway. The flush therefore consults this file too.
 *  2. `commit` sets `settledRef` FIRST. An early return after that point makes every later
 *     dismissal a no-op and the popup permanently unclosable.
 *  3. `useOutsideDismiss` arms its click swallower BEFORE calling back, so a
 *     refusal that rendered nothing would eat the realtor's click and look like a dead UI.
 *     The red field is what makes the swallowed click legible.
 *
 * ── Empty is always allowed ─────────────────────────────────────────────────────
 *
 * CLEARING A FIELD IS NOT AN INVALID VALUE, and treating it as one would make a wrong
 * address impossible to delete. Only a non-empty value that fails its own shape complains.
 */

export type CellDraftProblem = string | null;

/**
 * What is wrong with the current draft for this dimension, in Hebrew, or null.
 *
 * Pure and exported so the control can render it, the dismissal can consult it, and the
 * unmount flush can refuse on it — three call sites, one answer, no possibility of the
 * surface saying one thing while the write does another.
 */
export function describeCellDraftProblem(
  dimension: CrmEditableDimension,
  draft: CrmCellDraft,
  /**
   * What the field held when the editor opened.
   *
   * ⚠️ AN UNTOUCHED VALUE IS NEVER REFUSED, however malformed it is, and this exemption is
   * the difference between a guard and a trap. The corpus is full of lenient-era imports —
   * which is exactly why the SERVER declines to shape-validate these two fields at all —
   * so a contact can perfectly well be storing a ת״ז that fails its check digit. Without
   * this, opening that field to READ it and clicking away would refuse the dismissal, turn
   * the field red about a value the realtor never typed, and demand a second click to
   * escape a popup they only meant to glance at. Nothing would have been written either
   * way: an unchanged draft emits no key.
   *
   * The complaint is therefore about what the realtor is about to SAVE, not about what
   * they inherited. `EditContactDialog` draws the same line — it blocks on invalid AND
   * changed, never on invalid alone.
   */
  baseline: CrmCellDraft
): CellDraftProblem {
  const text = (draft.text ?? "").trim();
  if (!text) return null;
  if (text === (baseline.text ?? "").trim()) return null;

  if (dimension === "mail") {
    return isValidEmail(text) ? null : "כתובת אימייל לא תקינה";
  }

  if (dimension === "tehudatZehut") {
    /**
     * `isValidIsraeliId` UNCHANGED — no extra length rule of our own.
     *
     * It already accepts 5–9 digits and left-pads before running the check digit, which
     * is deliberate: the corpus carries short-form ids and the contact dialog validates
     * exactly this way. Adding a stricter "must be 9 digits" here would make the popup
     * refuse a value the dialog two clicks away accepts — the app disagreeing with itself
     * about one field, which is the specific outcome this file exists to prevent.
     */
    return isValidIsraeliId(text) ? null : "ת״ז לא תקינה — בדקו את הספרות";
  }

  return null;
}

/** Is this dimension one that can refuse at all? Cheap enough to ask per render. */
export const dimensionCanRefuse = (dimension: CrmEditableDimension): boolean =>
  dimension === "mail" || dimension === "tehudatZehut";
